import { useCallback, useEffect, useRef, useState } from 'react'
import { PublicKey, Transaction } from '@solana/web3.js'
import BN from 'bn.js'
import { getDepositIxs, getWithdrawIxs, getOrCreateATAInstruction } from '@jup-ag/lend/earn'
import { useAuth } from '../../auth/AuthContext'
import type { WalletBalance } from '../../types/wallet'
import { fetchJupLendPositions, fetchJupLendTokens, MINTS } from '../../api/trade'
import { newConnection } from '../../lib/tx'
import { useAdapterSignAndSubmit } from '../../lib/walletAdapter'
import { findBalance, formatBalance, formatUsdShort } from '../../hooks/useWalletBalance'
import { useSplBalance } from '../../hooks/useSplBalance'
import TokenIcon from '../common/TokenIcon'
import TradeResultBanner, { type TradeResult } from './TradeResultBanner'

const JUPSOL_MINT = MINTS.jupSOL
const JUPSOL_DECIMALS = 9
// Even though deposits move jupSOL (an SPL), the tx itself is paid in SOL —
// covers fee + jupSOL ATA rent if Jupiter Lend has to create the supply ATA.
const MIN_SOL_FOR_LEND = 0.01

function parseJup(raw: string): BN | null {
  if (!raw || !/^[0-9]*\.?[0-9]*$/.test(raw)) return null
  const [whole, frac = ''] = raw.split('.')
  const padded = (frac + '000000000').slice(0, JUPSOL_DECIMALS)
  try { return new BN((whole || '0') + padded) } catch { return null }
}

function fmtJup(raw: string | number): string {
  const n = typeof raw === 'string' ? Number(raw) : raw
  return (n / 1e9).toFixed(4)
}

interface Props {
  balances: WalletBalance[] | null
  defaultAmount?: string
}

export default function LendForm({ balances, defaultAmount }: Props) {
  const { portfolioId } = useAuth()
  const { signAndSubmit, publicKey, connected, friendlyTxError } = useAdapterSignAndSubmit()

  const [mode, setMode]     = useState<'deposit' | 'withdraw'>('deposit')
  const [amount, setAmount] = useState(defaultAmount ?? '')
  const [currentLent, setCurrentLent] = useState<string>('0')
  const [supplyApy, setSupplyApy] = useState<number | null>(null)
  const [result, setResult] = useState<TradeResult>({ kind: 'idle' })

  const walletJupSol = findBalance(balances, JUPSOL_MINT)   // merged total, display only
  const splJup = useSplBalance(publicKey, JUPSOL_MINT)       // authoritative wallet ATA balance
  const lastMaxRef = useRef<{ raw: BN; decimal: string } | null>(null)

  const reloadPositions = useCallback(async () => {
    if (!portfolioId) return
    const positions = await fetchJupLendPositions(portfolioId)
    const jupSolPos = positions.find(p => p.token?.assetAddress === JUPSOL_MINT)
    setCurrentLent(jupSolPos?.underlyingAssets ?? '0')
  }, [portfolioId])

  const reloadSupplyApy = useCallback(async () => {
    const tokens = await fetchJupLendTokens()
    const jupSolTok = tokens.find(t => t.assetAddress === JUPSOL_MINT || t.asset?.address === JUPSOL_MINT)
    if (jupSolTok?.totalRate) setSupplyApy(Number(jupSolTok.totalRate))
  }, [])

  useEffect(() => { reloadPositions() }, [reloadPositions])
  useEffect(() => { reloadSupplyApy() }, [reloadSupplyApy])

  const submit = async () => {
    const bnParsed = parseJup(amount)
    if (!bnParsed || bnParsed.isZero()) return
    setResult({ kind: 'pending', label: 'Building transaction…' })
    try {
      if (!connected || !publicKey) throw new Error('Wallet not connected')
      const signer = publicKey
      if (portfolioId && signer.toBase58() !== portfolioId) {
        throw new Error('Connected wallet does not match your session')
      }

      // Deposit pre-flight: use exact raw BN if MAX still populated; guard against over-balance.
      let bn: BN = bnParsed
      if (mode === 'deposit') {
        if (lastMaxRef.current && amount === lastMaxRef.current.decimal) {
          bn = lastMaxRef.current.raw
        }
        if (!splJup.loading && bn.gt(splJup.raw)) {
          const want = (Number(bn.toString()) / 1e9).toFixed(6)
          const have = (Number(splJup.raw.toString()) / 1e9).toFixed(6)
          throw new Error(`Amount ${want} jupSOL exceeds wallet ATA balance ${have} jupSOL. Click MAX to auto-populate.`)
        }
      }

      const connection = newConnection()
      const asset = new PublicKey(JUPSOL_MINT)
      let allIxs
      if (mode === 'deposit') {
        const { ixs } = await getDepositIxs({ amount: bn, asset, signer, connection })
        allIxs = ixs
      } else {
        const ataIxs = await getOrCreateATAInstruction(signer, asset, connection)
        const { ixs } = await getWithdrawIxs({ amount: bn, asset, signer, connection })
        allIxs = [...ataIxs, ...ixs]
      }

      setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
      const tx = new Transaction().add(...allIxs)
      const submitted = await signAndSubmit(tx)

      setResult({
        kind: 'success',
        signature: submitted.signature,
        summary: `${mode === 'deposit' ? 'Supplied' : 'Withdrew'} ${amount} jupSOL`,
      })
      setAmount('')
      lastMaxRef.current = null
      setTimeout(reloadPositions, 4000)
    } catch (e) {
      setResult({ kind: 'error', message: friendlyTxError(e) })
    }
  }

  const onMax = () => {
    if (mode === 'deposit') {
      if (splJup.raw.isZero()) {
        setResult({ kind: 'error', message: 'Your wallet ATA has zero jupSOL. Stake SOL first.' })
        return
      }
      setAmount(splJup.decimal)
      lastMaxRef.current = { raw: splJup.raw, decimal: splJup.decimal }
    } else {
      setAmount(fmtJup(currentLent))
      lastMaxRef.current = null
    }
  }

  // SOL preflight — both deposit and withdraw still pay tx fees from native
  // SOL even though the moved asset is jupSOL. A new ATA on either side adds
  // ~0.00204 SOL on top.
  const walletSol     = findBalance(balances, MINTS.SOL)
  const solBalanceNum = parseFloat(walletSol?.amount ?? '0')
  const insufficientSolForFees = balances != null && solBalanceNum < MIN_SOL_FOR_LEND

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Card title="Wallet (spendable)">
          <div className="flex items-center gap-1.5">
            <TokenIcon symbol="jupSOL" mint={JUPSOL_MINT} size={16} />
            <span className="text-sm font-semibold text-ink">
              {splJup.loading ? '…' : formatBalance(splJup.decimal)} jupSOL
            </span>
          </div>
          <div className="mt-0.5 text-[10px] text-gray-500">
            {walletJupSol ? formatUsdShort(walletJupSol.usdValue) : '—'}
          </div>
        </Card>
        <Card title="Supplied collateral">
          <div className="flex items-center gap-1.5">
            <TokenIcon symbol="jupSOL" mint={JUPSOL_MINT} size={16} />
            <span className="text-sm font-semibold text-ink">{fmtJup(currentLent)} jupSOL</span>
          </div>
          <div className="mt-0.5 flex items-center justify-between text-[10px]">
            <span className="text-gray-500">Used as borrow collateral</span>
            {supplyApy != null && (
              <span className="font-semibold text-accent-green">+{(supplyApy * 100).toFixed(2)}% APY</span>
            )}
          </div>
        </Card>
      </div>

      <div className="flex gap-1 rounded-lg bg-surface-hover p-1">
        {(['deposit', 'withdraw'] as const).map(m => (
          <button key={m}
            onClick={() => { setMode(m); setAmount('') }}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold ${mode === m ? 'bg-accent-purple/25 text-ink' : 'text-ink-muted'}`}
          >{m === 'deposit' ? 'Deposit' : 'Withdraw'}</button>
        ))}
      </div>

      <div className="rounded-lg border border-fringe bg-surface-hover p-3">
        <div className="mb-1 flex items-center justify-between">
          <label className="text-[10px] uppercase tracking-wider text-gray-500">
            {mode === 'deposit' ? 'Supply amount (jupSOL)' : 'Withdraw amount (jupSOL)'}
          </label>
          {((mode === 'deposit' && !splJup.raw.isZero()) || (mode === 'withdraw' && Number(currentLent) > 0)) && (
            <button type="button" onClick={onMax} className="text-[10px] text-accent-purple hover:underline">
              MAX {mode === 'deposit' ? formatBalance(splJup.decimal) : fmtJup(currentLent)}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <TokenIcon symbol="jupSOL" mint={JUPSOL_MINT} size={18} />
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.0"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="flex-1 bg-transparent text-lg font-semibold text-ink outline-none"
          />
        </div>
      </div>

      {insufficientSolForFees && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 leading-relaxed">
          Wallet has only {solBalanceNum.toFixed(4)} SOL — Jupiter Lend tx fees and any
          new ATA rent are paid from native SOL. Top up at least {MIN_SOL_FOR_LEND} SOL and retry.
        </div>
      )}

      <button
        type="button"
        disabled={result.kind === 'pending' || !parseJup(amount) || insufficientSolForFees}
        onClick={submit}
        className="w-full rounded-lg bg-accent-purple py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-accent-purple/40"
      >
        {result.kind === 'pending'
          ? 'Working…'
          : insufficientSolForFees ? `Top up SOL to ${mode}`
          : !connected ? (mode === 'deposit' ? 'Connect & Deposit' : 'Connect & Withdraw')
          : mode === 'deposit' ? 'Sign & Deposit' : 'Sign & Withdraw'}
      </button>

      <TradeResultBanner result={result} onDismiss={() => setResult({ kind: 'idle' })} />
    </div>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-surface-hover p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{title}</div>
      <div className="mt-1">{children}</div>
    </div>
  )
}
